import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Shield, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const emptyPerms = (pages) =>
  Object.fromEntries(pages.map((p) => [p.key, { view: false, edit: false }]));

export default function RolesTab() {
  const [pages, setPages] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftPerms, setDraftPerms] = useState({}); // {roleId: permissions}
  const [draftMeta, setDraftMeta] = useState({}); // {roleId: {name, description}}
  const [addOpen, setAddOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "" });
  const [confirmDel, setConfirmDel] = useState(null); // role to delete

  async function load() {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.get("/roles/pages"), api.get("/roles")]);
      setPages(p.data);
      setRoles(r.data);
      // Reset drafts
      const dp = {}; const dm = {};
      r.data.forEach((role) => {
        dp[role.id] = JSON.parse(JSON.stringify(role.permissions));
        dm[role.id] = { name: role.name, description: role.description || "" };
      });
      setDraftPerms(dp);
      setDraftMeta(dm);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function togglePerm(roleId, pageKey, field) {
    setDraftPerms((prev) => {
      const next = { ...prev };
      const rolePerms = { ...(next[roleId] || {}) };
      const cur = { ...(rolePerms[pageKey] || { view: false, edit: false }) };
      cur[field] = !cur[field];
      // edit implies view
      if (field === "edit" && cur.edit) cur.view = true;
      // disabling view also disables edit
      if (field === "view" && !cur.view) cur.edit = false;
      rolePerms[pageKey] = cur;
      next[roleId] = rolePerms;
      return next;
    });
  }

  async function saveRole(role) {
    try {
      const body = {
        name: draftMeta[role.id]?.name || role.name,
        description: draftMeta[role.id]?.description || "",
        permissions: draftPerms[role.id] || role.permissions,
      };
      await api.put(`/roles/${role.id}`, body);
      toast.success(`Saved · ${role.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save role");
    }
  }

  async function deleteRole(role) {
    setConfirmDel(null);
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  async function createRole() {
    if (!newRole.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post("/roles", {
        name: newRole.name.trim(),
        description: newRole.description.trim(),
        permissions: emptyPerms(pages),
      });
      toast.success("Role created");
      setAddOpen(false);
      setNewRole({ name: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500">
          Define per-page <span className="font-bold text-slate-700">View / Edit</span> access for each role.
          System roles cannot be deleted; permissions remain editable except for Super Admin.
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sky-500 hover:bg-sky-600 rounded-xl" data-testid="add-role-btn">
              <Plus className="h-4 w-4 mr-1.5"/>Add Custom Role
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Custom Role</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Role Name *</Label>
                <Input value={newRole.name} onChange={(e)=>setNewRole({...newRole, name: e.target.value})}
                  placeholder="e.g. Quality Auditor" data-testid="new-role-name"/>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
                <Input value={newRole.description} onChange={(e)=>setNewRole({...newRole, description: e.target.value})}
                  placeholder="What does this role do?" data-testid="new-role-desc"/>
              </div>
              <div className="text-xs text-slate-500">Permissions start as none — toggle them after the role is created.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setAddOpen(false)}>Cancel</Button>
              <Button onClick={createRole} className="bg-sky-500 hover:bg-sky-600" data-testid="create-role-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading && <div className="text-sm text-slate-400">Loading…</div>}

      <div className="grid gap-4">
        {roles.map((role) => {
          const isSuperAdmin = role.key === "super_admin";
          const dp = draftPerms[role.id] || role.permissions;
          const dm = draftMeta[role.id] || { name: role.name, description: role.description };
          const dirty = JSON.stringify(dp) !== JSON.stringify(role.permissions)
            || dm.name !== role.name || (dm.description || "") !== (role.description || "");
          return (
            <Card key={role.id} className="border-slate-200 rounded-2xl" data-testid={`role-card-${role.key}`}>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-sky-50 grid place-items-center text-sky-600">
                      <Shield className="h-5 w-5"/>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {role.is_system && !isSuperAdmin ? (
                          <div className="font-display font-black text-lg text-slate-900">{role.name}</div>
                        ) : isSuperAdmin ? (
                          <div className="font-display font-black text-lg text-slate-900">{role.name}</div>
                        ) : (
                          <Input
                            value={dm.name}
                            onChange={(e)=>setDraftMeta((p)=>({...p, [role.id]: { ...dm, name: e.target.value }}))}
                            className="h-8 max-w-[260px] font-display font-black text-base"
                            data-testid={`role-name-${role.key}`}
                          />
                        )}
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600 font-mono text-[10px]">{role.key}</Badge>
                        {role.is_system && (
                          <Badge variant="secondary" className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] gap-1">
                            <Lock className="h-3 w-3"/>System
                          </Badge>
                        )}
                      </div>
                      <Input
                        value={dm.description}
                        onChange={(e)=>setDraftMeta((p)=>({...p, [role.id]: { ...dm, description: e.target.value }}))}
                        placeholder="Description"
                        className="h-7 text-xs text-slate-500 border-slate-200 max-w-[420px]"
                        disabled={isSuperAdmin}
                        data-testid={`role-desc-${role.key}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={()=>saveRole(role)}
                      disabled={!dirty || isSuperAdmin}
                      className="bg-sky-500 hover:bg-sky-600 rounded-xl h-9"
                      data-testid={`save-role-${role.key}`}
                    >
                      <Save className="h-4 w-4 mr-1.5"/>Save
                    </Button>
                    {!role.is_system && (
                      <Button variant="ghost" onClick={()=>setConfirmDel(role)} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9" data-testid={`delete-role-${role.key}`}>
                        <Trash2 className="h-4 w-4"/>
                      </Button>
                    )}
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2">Page</th>
                        <th className="px-4 py-2 w-[100px]">View</th>
                        <th className="px-4 py-2 w-[100px]">Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pages.map((p) => {
                        const v = dp[p.key]?.view || false;
                        const e = dp[p.key]?.edit || false;
                        return (
                          <tr key={p.key} className="border-t border-slate-100">
                            <td className="px-4 py-2 font-medium text-slate-800">{p.label}</td>
                            <td className="px-4 py-2">
                              <Switch
                                checked={v}
                                onCheckedChange={()=>togglePerm(role.id, p.key, "view")}
                                disabled={isSuperAdmin}
                                data-testid={`perm-${role.key}-${p.key}-view`}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Switch
                                checked={e}
                                onCheckedChange={()=>togglePerm(role.id, p.key, "edit")}
                                disabled={isSuperAdmin || !v}
                                data-testid={`perm-${role.key}-${p.key}-edit`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent data-testid="role-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role &ldquo;{confirmDel?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Employees currently mapped to this role will be revalidated against system defaults.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDel && deleteRole(confirmDel)}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="role-delete-confirm-btn"
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
